import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { get, post } from "../utils/api";
import Button from "../components/Button/Button";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CirclePlus, SquarePen, Save, X } from "lucide-react";
import ReusableTable from "../components/Table/ReusableTable";
import { Dropdown } from "primereact/dropdown";

const formatPercentValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return String(value);
};

const InputField = React.memo(
  ({
    label,
    value,
    onChange,
    readOnly = false,
    className = "",
    isNumeric = false,
  }) => (
    <div className="flex flex-col">
      <label className="text-sm font-medium mb-1">{label}</label>
      <input
        type="text"
        inputMode={isNumeric ? "decimal" : "text"}
        value={value ?? ""}
        onChange={onChange}
        readOnly={readOnly}
        className={`border rounded-md p-2 w-full ${readOnly ? "bg-gray-100" : ""
          } ${className}`}
        onKeyPress={(e) => {
          if (isNumeric) {
            // Allow numbers, decimal point, and control keys only for numeric fields
            if (
              !/[\d.]/.test(e.key) &&
              e.key !== "Backspace" &&
              e.key !== "Delete" &&
              e.key !== "Tab" &&
              e.key !== "Enter"
            ) {
              e.preventDefault();
            }

            // Allow only one decimal point
            if (e.key === "." && e.target.value.includes(".")) {
              e.preventDefault();
            }
          }
        }}
        onPaste={(e) => {
          if (isNumeric) {
            // Prevent paste if it contains non-numeric characters for numeric fields
            const pasteData = e.clipboardData.getData("text");
            if (!/^[\d.]*$/.test(pasteData)) {
              e.preventDefault();
              toast.error("Only numbers and decimal points are allowed");
            }
          }
        }}
      />
    </div>
  )
);

const normalizeArnCode = (value) => {
  const digitsOnly = String(value || "").replace(/\D/g, "").slice(0, 5);
  return digitsOnly ? `APRN${digitsOnly}` : "APRN";
};

const isValidArnCode = (value) => /^APRN\d{5}$/.test(String(value || "").trim());

const SalesDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [distributor, setDistributor] = useState("");
  const [arnCode, setArnCode] = useState("APRN");
  const [address, setAddress] = useState("");
  const [pinCode, setPinCode] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("India");
  const isIndia = !country || country.trim().toUpperCase() === "INDIA";
  const countryOptions = ["India", "Outside India"];
  const [pinLookupLoading, setPinLookupLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [panNumber, setPanNumber] = useState("");
  const [arnCodeError, setArnCodeError] = useState("");
  const [isArnEditable, setIsArnEditable] = useState(true);
  const [isPanEditable, setIsPanEditable] = useState(true);
  const [distributorList, setDistributorList] = useState([]);
  const [approvedFunds, setApprovedFunds] = useState([]);
  const [approvedFundsLoading, setApprovedFundsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [distributor_id, setDistributorId] = useState(0);
  const [distributorSequenceNumber, setDistributorSequenceNumber] = useState("");
  const isDistributorReadOnly = !!location.state?.distributorData;

  // Handle incoming edit data
  useEffect(() => {
    if (location.state?.distributorData) {
      const data = location.state.distributorData;
      console.log("Edit Data Received:", data);

      setDistributor(data.distributor_name || "");
      setApprovedFunds([]);
      setArnCode(normalizeArnCode(data.arn_code));
      setArnCodeError("");
      setEmail(data.email || "");
      setDistributorSequenceNumber(data.distributor_sequence_number || "");
      // Fetch full details including products
      if (data.distributor_sequence_number) {
        handleView({ distributor_sequence_number: data.distributor_sequence_number });
      }
    }
  }, [location.state]);

  const fetchApprovedFundsByDistributor = useCallback(async (distributorName) => {
    if (!distributorName) {
      setApprovedFunds([]);
      return;
    }

    try {
      setApprovedFundsLoading(true);
      const response = await post("/sales_listing/fetchApprovedFundsByDistributorName", {
        distributor_name: distributorName,
      });

      if (response.data?.Success && Array.isArray(response.data.Data)) {
        setApprovedFunds(response.data.Data);
      } else {
        setApprovedFunds([]);
      }
    } catch (error) {
      console.error("Error fetching approved funds:", error);
      setApprovedFunds([]);
    } finally {
      setApprovedFundsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (distributor) {
      fetchApprovedFundsByDistributor(distributor);
    } else {
      setApprovedFunds([]);
    }
  }, [distributor, fetchApprovedFundsByDistributor]);

  useEffect(() => {
    if (isDistributorReadOnly) return;

    // Outside India: postal code, city and state are entered manually
    if (!isIndia) {
      setPinLookupLoading(false);
      return;
    }

    const normalizedPin = String(pinCode || "").replace(/\D/g, "").slice(0, 6);

    if (normalizedPin !== pinCode) {
      setPinCode(normalizedPin);
      return;
    }

    if (normalizedPin.length === 0) {
      setCity("");
      setState("");
      setPinLookupLoading(false);
      return;
    }

    if (normalizedPin.length < 6) {
      setCity("");
      setState("");
      setPinLookupLoading(false);
      return;
    }

    let cancelled = false;

    const lookupPinCode = async () => {
      try {
        setPinLookupLoading(true);
        const response = await fetch(`https://api.postalpincode.in/pincode/${normalizedPin}`);
        const result = await response.json();
        const office = result?.[0]?.PostOffice?.[0];

        if (!cancelled && office) {
          setCity(office.District || office.Block || office.Name || "");
          setState(office.State || "");
          return;
        }

        if (!cancelled) {
          setCity("");
          setState("");
        }
      } catch (error) {
        console.error("PIN lookup failed:", error);
        if (!cancelled) {
          setCity("");
          setState("");
        }
      } finally {
        if (!cancelled) {
          setPinLookupLoading(false);
        }
      }
    };

    lookupPinCode();

    return () => {
      cancelled = true;
    };
  }, [pinCode, isDistributorReadOnly, isIndia]);

  const handleCountryChange = (value) => {
    if (!value || value === country) return;
    setCountry(value);
    setPinCode("");
    setCity("");
    setState("");
  };

  const handleView = async (row) => {
    try {
      const response = await post("/sales_listing/fetchDistributorWithProducts", {
        distributor_sequence_number: row.distributor_sequence_number
      });

      if (response.data?.Success && response.data.Data) {
        const details = response.data.Data[0]; // Assuming array returned
        if (details) {
          setDistributorId(details.distributor_id || 0); // Need ID for saving
          setAddress(details.address || details.distributor_address || "");
          setCountry(details.country || "India");
          setCity(details.city || "");
          setState(details.state || "");
          setPinCode(details.pincode || "");
          setPanNumber(details.pan_number || "");

          // Populate saved tables
          const products = response.data.Data; // All rows are product rows joined

          if (products && products.length > 0) {
            const aifII = products.filter(p => p.product_type_id === 1).map(p => ({
              fund_name: p.fund_name,
              class: p.class_code,
              size: p.fund_size || p.size || "",
              management_fee: p.management_fee_sharing_per,
              placement_fee: p.placement_fee_per,
              upfront_fee: p.upfront_fee_per,
              trail_fee: p.trail_fee_sharing_per || p.trail_fee_per,
              total_fee: p.total_fee_per,
              product_id: p.product_id,
              fund_id: p.fund_id,
              is_active: true
            }));
            setSavedAifII(aifII);

            const aifIII = products.filter(p => p.product_type_id === 2).map(p => ({
              fund_name: p.fund_name,
              class: p.class_code,
              management_fee: p.management_fee_sharing_per,
              performance_fee: p.performance_fee_sharing_per,
              product_id: p.product_id,
              fund_id: p.fund_id,
              is_active: true
            }));
            setSavedAifIII(aifIII);

            const pms = products.filter(p => p.product_type_id === 3).map(p => {
              console.log("PMS Data checking:", p);
              return {
                fund_name: p.fund_name,
                class: p.class_code,
                management_fee: p.management_fee_sharing_per,
                management_fee_more_than_15cr: p.management_fee_sharing_per_morethan15Cr || p.management_fee_sharing_per_morethan15cr, // Try both cases
                distributor_sharing_fee: p.performance_fee_sharing_per, // Backend stores it in performance_fee column for PMS
                product_id: p.product_id,
                fund_id: p.fund_id,
                is_active: true
              };
            });
            setSavedPms(pms);

            // Freeze fund type based on existing products
            if (aifII.length > 0 || aifIII.length > 0) {
              setFundType("aif");
              setFundTypeForApi("aif");
            } else if (pms.length > 0) {
              setFundType("pms");
              setFundTypeForApi("pms");
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching details for edit:", error);
      toast.error("Failed to load existing fund details");
    }
  };

  const generateDistributorSequence = (name) => {
    if (!name) return "";
    // Take first 4 characters
    let prefix = name.slice(0, 4).toUpperCase();
    // Replace spaces with 1
    prefix = prefix.replace(/\s/g, "1");
    // Unique number
    const unique = Date.now().toString().slice(-6);
    return `${prefix}${unique}`;
  };

  // fund type
  const [fundType, setFundType] = useState("");
  const [fundTypeForApi, setFundTypeForApi] = useState("");
  const [selectedFundName, setSelectedFundName] = useState("");
  console.log(selectedFundName, 'selected fund name');

  const [classCodes, setClassCodes] = useState([]);
  const [selectedClassCode, setSelectedClassCode] = useState("");
  const [fundTableType, setFundTableType] = useState("");

  const [fundNames, setFundNames] = useState([]);
  console.log(fundNames, '193');

  const [aifCatIIData, setAifCatIIData] = useState([
    {
      fund_name: "",
      class: "",
      size: "",
      management_fee: "",
      placement_fee: "",
      upfront_fee: "",
      trail_fee: "",
      total_fee: "",
    },
  ]);
  const [aifCatIIIData, setAifCatIIIData] = useState([
    { fund_name: "", class: "", management_fee: "", performance_fee: "" },
  ]);

  const [pmsData, setPmsData] = useState([
    {
      fund_name: "",
      class: "",
      management_fee: "",
      management_fee_more_than_15cr: "",
      distributor_sharing_fee: "",
    },
  ]);

  // States for storing saved fund details
  const [savedAifII, setSavedAifII] = useState([]);
  const [savedAifIII, setSavedAifIII] = useState([]);
  const [savedPms, setSavedPms] = useState([]);
  const [savedFund, setSavedFund] = useState([]);

  const [selectedFundProductId, setSelectedFundProductId] = useState("");

  const sortedDistributorList = useMemo(
    () =>
      [...distributorList].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        })
      ),
    [distributorList]
  );

  const userDetails = JSON.parse(localStorage.getItem("user_details") || "{}");
  const roleId = userDetails?.role_id;
  const userEmail = userDetails?.email;
  const userId = userDetails?.id || userDetails?.user_Id;

  // Match DigitalForm readOnly logic
  const isReadOnly = false; // SalesDashboard always allows editing




  /* Fetch distributors */
  useEffect(() => {
    const fetchDistributors = async () => {
      try {
        const response = await get("/sales_listing/fetchDistributors");
        const users = Array.isArray(response.data?.Data)
          ? response.data.Data
          : [];

        setDistributorList(
          users.map((u) => ({
            id: u.id,
            name: u.distributor_name,
            arn_code: u.arn_code,
            email: u.distributor_email,
            address: u.address,
            pincode: u.pincode,
            city: u.city,
            state: u.state,
            country: u.country,
            pan_number: u.pan_number,
            distributor_sequence_number: u.distributor_sequence_number,
          }))
        );
      } catch (error) {
        console.error("Error fetching distributors:", error);
        setDistributorList([]);
      }
    };

    fetchDistributors();
  }, []);

  const handleDistributorChange = (name) => {
    setDistributor(name);
    setApprovedFunds([]);

    const dist = distributorList.find((item) => item.name === name);
    setDistributorId(dist?.id || 0);

    if (dist?.arn_code) {
      setArnCode(normalizeArnCode(dist.arn_code));
      setArnCodeError("");
      setIsArnEditable(false);
    } else {
      setArnCode("APRN");
      setArnCodeError("");
      setIsArnEditable(true);
    }

    if (dist?.pan_number) {
      setPanNumber(dist.pan_number);
      setIsPanEditable(false);
    } else {
      setPanNumber("");
      setIsPanEditable(true);
    }

    setAddress(dist?.address || "");
    setCountry(dist?.country || "India");
    setPinCode(dist?.pincode || "");
    setCity(dist?.city || "");
    setState(dist?.state || "");
    setEmail(dist?.email || "");

    if (name) {
      const seq = generateDistributorSequence(name);
      setDistributorSequenceNumber(seq);
      console.log("Generated Sequence Number:", seq);
    } else {
      setAddress("");
      setPinCode("");
      setCity("");
      setState("");
      setEmail("");
      setPanNumber("");
      setArnCode("APRN");
      setArnCodeError("");
      setDistributorId(0);
      setDistributorSequenceNumber("");
    }
  };

  const handleArnCodeChange = (e) => {
    const normalizedValue = normalizeArnCode(e.target.value);
    setArnCode(normalizedValue);

    if (!normalizedValue || normalizedValue === "APRN" || isValidArnCode(normalizedValue)) {
      setArnCodeError("");
    }
  };

  const validateArnCode = () => {
    if (!arnCode || arnCode === "APRN") {
      setArnCodeError("");
      return true;
    }

    if (!isValidArnCode(arnCode)) {
      setArnCodeError("Enter APRN code in `APRN12345` format.");
      return false;
    }

    setArnCodeError("");
    return true;
  };

  /* Send onboarding email */
  const handleSubmit = async () => {
    // PAN is mandatory only for Indian distributors
    if (!distributor || !address || !pinCode || !city || !state || !country || !email || (isIndia && !panNumber)) {
      toast.info("Please fill in all fields.");
      return;
    }

    if (!validateArnCode()) {
      toast.error("Please enter APRN code in `APRN12345` format.");
      return;
    }

    // PAN Validation
    const panRegex = /[A-Z]{5}[0-9]{4}[A-Z]{1}/;
    if (isIndia && !panRegex.test(panNumber)) {
      toast.error("Invalid PAN Number format (e.g. ABCDE1234F)");
      return;
    }

    const emailList = email
      .split(";")
      .map((e) => e.trim())
      .filter(Boolean);

    const invalidEmails = emailList.filter(
      (e) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)
    );

    if (invalidEmails.length) {
      toast.error(`Invalid email(s): ${invalidEmails.join("; ")}`);
      return;
    }

    const selectedDistributor = distributorList.find(
      (d) => d.name === distributor
    );

    const payload = {
      distributor_name: distributor,
      arn_code: arnCode === "APRN" ? "" : arnCode.toUpperCase(),
      distributor_address: address,
      pin_code: pinCode,
      city: city,
      state: state,
      country: country.trim(),
      distributor_email: emailList.join(";"),
      login_email: selectedDistributor?.email || "",
      sales_name: userDetails?.name || userDetails?.username || "",
      pan_number: panNumber.toUpperCase(),
      user_Id: userId,
    };

    try {
      setLoading(true);
      const response = await post(
        "/onboarding/sendOnboardingDistributorLink",
        payload
      );

      if (response.data?.Success) {
        toast.success("Onboarding email(s) sent successfully!");
        setDistributor("");
        setArnCode("APRN");
        setAddress("");
        setEmail("");
        setIsArnEditable(true);
      } else {
        toast.error(response.data?.Message || "Failed to send emails.");
      }
    } catch (error) {
      console.error("Error sending onboarding email:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitWithFunds = async () => {

    console.log("Form State:", { distributor, arnCode, address, pinCode, city, state, email });

    // PAN is mandatory only for Indian distributors
    if (!distributor || !address || !pinCode || !city || !state || !country || !email || (isIndia && !panNumber)) {
      toast.error(`Please fill in all fields: Distributor, Address, Country, Pin Code, City, State, Email${isIndia ? ", and PAN" : ""}.`);
      console.warn("❌ Validation failed: Missing fields");
      return;
    }

    if (!validateArnCode()) {
      toast.error("Please enter APRN code in `APRN12345` format.");
      return;
    }

    // PAN Validation
    const panRegex = /[A-Z]{5}[0-9]{4}[A-Z]{1}/;
    if (isIndia && !panRegex.test(panNumber)) {
      toast.error("Invalid PAN Number format (e.g. ABCDE1234F)");
      return;
    }

    const emailList = email
      .split(";")
      .map((e) => e.trim())
      .filter(Boolean);

    const invalidEmails = emailList.filter(
      (e) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)
    );

    if (invalidEmails.length) {
      toast.error(`Invalid email(s): ${invalidEmails.join("; ")}`);
      return;
    }

    const selectedDistributor = distributorList.find(
      (d) => d.name === distributor
    );

    // Collect all fund details
    const allFunds = [
      ...savedAifII.map((fund) => ({
        product_type_id: 1, // AIF CAT II
        fund_id: fund.fund_id,
        fund_name: fund.fund_name,
        class_code: fund.class,
        placement_fee_per: fund.placement_fee,
        upfront_fee_per: fund.upfront_fee,
        trail_fee_per: fund.trail_fee,
        management_fee_sharing_per: fund.management_fee,
        performance_fee_sharing_per: null,
        total_fee: fund.total_fee,
        scheme_size: fund.size,
      })),
      ...savedAifIII.map((fund) => ({
        product_type_id: 2, // AIF CAT III
        fund_id: fund.fund_id,
        fund_name: fund.fund_name,
        class_code: fund.class,
        placement_fee_per: null,
        upfront_fee_per: null,
        trail_fee_per: null,
        management_fee_sharing_per: fund.management_fee,
        performance_fee_sharing_per: fund.performance_fee,
        total_fee: null,
      })),
      ...savedPms.map((fund) => ({
        product_type_id: 3, // PMS
        fund_id: fund.fund_id,
        fund_name: fund.fund_name,
        class_code: fund.class,
        placement_fee_per: null,
        upfront_fee_per: null,
        trail_fee_per: null,
        management_fee_sharing_per: fund.management_fee,
        management_fee_sharing_more_than_15cr_per:
          fund.management_fee_more_than_15cr, // New field map
        distributor_sharing_fee_per: fund.distributor_sharing_fee, // New field map
        performance_fee_sharing_per: null,
        total_fee: null,
      })),
    ];

    if (allFunds.length === 0 && !isReadOnly) {
      const confirmSubmit = window.confirm(
        "No funds added. Are you sure you want to submit details only?"
      );
      if (!confirmSubmit) return;
    }

    const payload = {
      distributor_name: distributor,
      arn_code: arnCode === "APRN" ? "" : arnCode.toUpperCase(),
      distributor_address: address,
      pin_code: pinCode,
      city: city,
      state: state,
      country: country.trim(),
      distributor_email: emailList.join(";"),
      login_email: selectedDistributor?.email || "",
      sales_name: userDetails?.name || userDetails?.username || "",
      pan_number: panNumber.toUpperCase(), // Add PAN to payload
      products: allFunds,
      distributor_sequence_number: distributorSequenceNumber,
      user_Id: userId,
    };

    try {
      setLoading(true);
      const response = await post(
        "/onboarding/sendOnboardingDistributorLink",
        payload
      );

      if (response.data?.Success) {
        toast.success("Onboarding email(s) sent successfully with fund details!");
        setDistributor("");
        setArnCode("APRN");
        setAddress("");
        setEmail("");
        setDistributorSequenceNumber("");
        setIsArnEditable(true);
        // Clear fund tables
        setSavedAifII([]);
        setSavedAifIII([]);
        setSavedPms([]);
        setSavedFund([]);

        navigate("/sales_listing");
      } else {
        toast.error(response.data?.Message || "Failed to send emails.");
      }
    } catch (error) {
      console.error("Error sending onboarding email with funds:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch fund names based on selected fund type (SAME AS DigitalForm)
  const fetchFundNames = async (fundType) => {
    if (!fundType) {
      setFundNames([]);
      setSelectedFundName("");
      return;
    }

    const payload = {
      distributor_id: "0",
      product_type: fundType,
    };

    try {
      const res = await post("/onboarding/getFundNamesForDistributor", payload);
      const data = res?.data?.Data || [];
      console.log("Received fund names:", data);

      console.log("Fetched fund names:", data);

      if (Array.isArray(data) && data.length > 0) {
        const mappedFunds = data.map((f, idx) => ({
          fund_id: String(f.fund_id || idx + 1),
          fund_name: f.fund_name || "Unnamed Fund",
        }));

        const approvedFundIds = new Set(
          approvedFunds.map((fund) => String(fund.fund_id))
        );

        const availableFunds = mappedFunds.filter(
          (fund) => !approvedFundIds.has(String(fund.fund_id))
        );

        setFundNames(availableFunds);

        if (
          selectedFundName &&
          !availableFunds.some((fund) => fund.fund_name === selectedFundName)
        ) {
          setSelectedFundName("");
        }
      } else {
        setFundNames([]);
        toast.warn("No funds found for this product type.");
      }
    } catch (err) {
      console.error("fetchFundNames error:", err);
      toast.error("Failed to fetch fund names. Please try again.");
      setFundNames([]);
    }
  };

  // Fetch class codes by fund name and populate table (Refactored)
  const fetchClassCodes = async (fundName) => {
    if (!fundName) {
      setClassCodes([]);
      setSelectedClassCode("");
      setFundType("");
      return;
    }

    const payload = {
      fund_name: fundName,
    };

    try {
      const res = await post("/onboarding/fetchClassCodeByFundName", payload);
      const data = res?.data?.Data || [];

      if (Array.isArray(data) && data.length > 0) {
        const mapped = data.map((c) => ({
          class_code: c.class_code || "",
          class_name: c.class_name || "",
          product_type_id: c.product_type_id || null,
          size: c.size,
          management_fee: c.management_fee || c.management_fee_sharing_per,
          performance_fee: "", // Explicitly empty as per requirement
          placement_fee: c.placement_fee_per,
          upfront_fee: c.upfront_fee_per,
          trail_fee: c.trail_fee_per
        }));
        setClassCodes(mapped);

        // Reset all tables first
        setAifCatIIData([]);
        setAifCatIIIData([]);
        setPmsData([]);

        let hasAif = false;
        let hasPms = false;

        // Populate table data directly based on all available classes
        const catIIClasses = mapped.filter(c => Number(c.product_type_id) === 1);
        const catIIIClasses = mapped.filter(c => Number(c.product_type_id) === 2);
        const pmsClasses = mapped.filter(c => Number(c.product_type_id) === 3);

        if (catIIClasses.length > 0) {
          const newRows = catIIClasses.map(c => ({
            fund_name: fundName,
            class: c.class_code,
            size: c.size || "",
            management_fee: c.management_fee || "",
            placement_fee: c.placement_fee || "",
            upfront_fee: c.upfront_fee || "",
            trail_fee: c.trail_fee || "",
            total_fee: "",
          }));
          setAifCatIIData(newRows);
          hasAif = true;
        }

        if (catIIIClasses.length > 0) {
          const newRows = catIIIClasses.map(c => ({
            fund_name: fundName,
            class: c.class_code,
            fund_name: fundName,
            class: c.class_code,
            management_fee: c.management_fee || "",
            performance_fee: "" // Explicitly empty
          }));
          setAifCatIIIData(newRows);
          hasAif = true;
        }

        if (pmsClasses.length > 0) {
          const newRows = pmsClasses.map(c => {
            let management_fee = "";
            let management_fee_more_than_15cr = "";

            // Check size value case-insensitively
            const sizeValue = (c.size || "").toLowerCase();

            if (sizeValue.includes("more")) {
              management_fee_more_than_15cr = c.management_fee || "";
            } else if (sizeValue.includes("less")) {
              management_fee = c.management_fee || "";
            }

            return {
              fund_name: fundName,
              class: c.class_code,
              management_fee,
              management_fee_more_than_15cr,
              distributor_sharing_fee: "",
            };
          });

          setPmsData(newRows);
          hasPms = true;
        }

        if (hasAif) setFundType("aif");
        else if (hasPms) setFundType("pms");
        else setFundType("");

      } else {
        setClassCodes([]);
        setFundType("");
        toast.warn("No class codes found for this fund.");
      }
    } catch (err) {
      console.error("fetchClassCodes error:", err);
      toast.error("Failed to fetch class codes. Please try again.");
      setClassCodes([]);
      setFundType("");
    }
  };

  // Trigger API when fundTypeForApi changes (SAME AS DigitalForm)
  useEffect(() => {
    if (fundTypeForApi) {
      fetchFundNames(fundTypeForApi);
    }
  }, [fundTypeForApi, approvedFunds]);

  // useEffect to fetch class codes when fund name changes (SAME AS DigitalForm)
  useEffect(() => {
    if (selectedFundName) {
      fetchClassCodes(selectedFundName);
    } else {
      setClassCodes([]);
      setSelectedClassCode("");
    }
  }, [selectedFundName]);



  // fund type functions
  const handleFundInputChange = useCallback(
    (tableType, index, field, value) => {
      // Define which fields should be numeric only (all fee fields except 'size')
      const numericFields = [
        "management_fee",
        "management_fee_more_than_15cr",
        "placement_fee",
        "upfront_fee",
        "trail_fee",
        "performance_fee",
        "distributor_sharing_fee",
      ];

      // Only apply numeric validation to fee fields, NOT to 'size' field
      if (numericFields.includes(field)) {
        // Remove any non-numeric characters except decimal point
        value = value.replace(/[^\d.]/g, "");

        // Ensure only one decimal point
        const decimalCount = (value.match(/\./g) || []).length;
        if (decimalCount > 1) {
          value = value.substring(0, value.lastIndexOf("."));
        }

        // Limit to 3 decimal places
        if (value.includes(".")) {
          const parts = value.split(".");
          if (parts[1].length > 3) {
            value = `${parts[0]}.${parts[1].substring(0, 3)}`;
          }
        }

        // Limit whole number part to reasonable length
        if (!value.includes(".")) {
          if (value.length > 5) {
            value = value.substring(0, 5);
          }
        } else {
          const parts = value.split(".");
          if (parts[0].length > 5) {
            value = `${parts[0].substring(0, 5)}.${parts[1] || ""}`;
          }
        }
      }

      let updated;
      let setDataFn;

      if (tableType === "aifII") {
        updated = [...aifCatIIData];
        setDataFn = setAifCatIIData;
      } else if (tableType === "aifIII") {
        updated = [...aifCatIIIData];
        setDataFn = setAifCatIIIData;
      } else if (tableType === "pms") {
        updated = [...pmsData];
        setDataFn = setPmsData;
      } else {
        return;
      }

      updated[index][field] = value;

      // ✅ NEW: Auto-calculate upfront and trail fees when placement fee changes for AIF CAT II
      if (tableType === "aifII" && field === "placement_fee") {
        const placementFee = parseFloat(value) || 0;

        if (placementFee > 0) {
          // Calculate upfront fee as 1/3 of placement fee
          const upfrontFee = (placementFee / 3).toFixed(3);
          // Calculate trail fee as placement fee minus upfront fee
          const trailFee = (placementFee - parseFloat(upfrontFee)).toFixed(3);

          updated[index].upfront_fee = upfrontFee;
          updated[index].trail_fee = trailFee;
        } else {
          // If placement fee is 0 or empty, clear the other fields
          updated[index].upfront_fee = "";
          updated[index].trail_fee = "";
        }
      }

      if (tableType === "aifII") {
        const row = updated[index];
        const placement = parseFloat(row.placement_fee || 0);
        const upfront = parseFloat(row.upfront_fee || 0);
        const trail = parseFloat(row.trail_fee || 0);

        if (!isNaN(placement) && !isNaN(upfront) && !isNaN(trail)) {
          row.total_fee = (placement + upfront + trail).toFixed(3);
        }
      }

      setDataFn(updated);
    },
    [aifCatIIData, aifCatIIIData, pmsData]
  );

  // Per-row Add function
  const handleRowAdd = async (tableType, index) => {
    if (isReadOnly) {
      toast.warn("Form is read-only. Action not allowed.");
      return;
    }

    try {
      const selectedFund = fundNames.find(
        (f) => f.fund_name === selectedFundName
      );

      console.log(selectedFund, 'selected fund 732');


      if (!selectedFund) {
        toast.error("Invalid fund selection.");
        return;
      }

      const num = (v) => {
        if (v === undefined || v === null) return null;
        const s = String(v).trim();
        if (s === "") return null;
        const n = parseFloat(s);
        return Number.isNaN(n) ? null : n;
      };

      const isValidPercentage = (v) => {
        if (v === undefined || v === null) return false;
        const n = parseFloat(String(v).trim());
        if (Number.isNaN(n)) return false;
        return n >= 0 && n <= 100;
      };

      let rowData = null;
      let product_details = [];

      if (tableType === "aifII") rowData = aifCatIIData[index];
      else if (tableType === "aifIII") rowData = aifCatIIIData[index];
      else if (tableType === "pms") rowData = pmsData[index];

      if (!rowData) return;

      const classCode = (rowData.class || "").toString().trim();

      if (tableType === "aifII") {
        const placement = num(rowData.placement_fee);
        const upload = num(rowData.upfront_fee);
        const trail = num(rowData.trail_fee);
        const size = rowData.size;

        if (placement === null || upload === null || trail === null) {
          toast.error(`Placement, Upfront and Trail fees are mandatory.`);
          return;
        }
        if (!isValidPercentage(placement) || !isValidPercentage(upload) || !isValidPercentage(trail)) {
          toast.error(`Fees must be numeric percentages between 0 and 100.`);
          return;
        }

        product_details.push({
          product_id: 1,
          product_type_id: 1,
          fund_id: selectedFund.fund_id,
          class_code: classCode,
          size: size,
          placement_fee_per: placement,
          upfront_fee_per: upload,
          trail_fee_per: trail,
          total_fee_per: rowData.total_fee,
          management_fee_sharing_per: num(rowData.management_fee),
          performance_fee_sharing_per: null,
          is_active: true,
        });

      } else if (tableType === "aifIII") {
        // Validation for AIF III if needed?
        // Assuming management_fee or performance_fee is enough to be valid?
        // Logic says "skip rows where ALL fee fields are empty"
        if (!rowData.management_fee && !rowData.performance_fee) {
          toast.error("Please enter at least one fee.");
          return;
        }

        product_details.push({
          product_id: 1,
          product_type_id: 2,
          fund_id: selectedFund.fund_id,
          class_code: classCode,
          size: null,
          placement_fee_per: null,
          upfront_fee_per: null,
          trail_fee_per: null,
          total_fee_per: null,
          management_fee_sharing_per: num(rowData.management_fee),
          performance_fee_sharing_per: num(rowData.performance_fee),
          is_active: true,
        });

      } else if (tableType === "pms") {
        if (!rowData.management_fee && !rowData.management_fee_more_than_15cr && !rowData.distributor_sharing_fee) {
          toast.error("Please enter at least one fee.");
          return;
        }

        product_details.push({
          product_id: 2,
          product_type_id: 3,
          fund_id: selectedFund.fund_id,
          class_code: classCode,
          size: null,
          placement_fee_per: null,
          upfront_fee_per: null,
          trail_fee_per: null,
          total_fee_per: null,
          management_fee_sharing_per: num(rowData.management_fee),
          management_fee_sharing_per_morethan15Cr: num(rowData.management_fee_more_than_15cr),
          performance_fee_sharing_per: num(rowData.distributor_sharing_fee),
          distributor_sharing_fee_per: num(rowData.distributor_sharing_fee),
          is_active: true,
        });
      }

      const payload = {
        distributor_id: distributor_id || 0,
        user_Id: userId,
        product_details: product_details,
        distributor_sequence_number: distributorSequenceNumber,
      };

      const res = await post(
        "/onboarding/saveDistributorProductDetails",
        payload
      );

      if (res.data?.Success) {
        toast.success("Fund details saved successfully");

        const saved = Array.isArray(res.data.Data) && res.data.Data.length
          ? res.data.Data
          : product_details;

        if (tableType === "aifII") {
          const savedII = saved.filter((s) => Number(s.product_type_id) === 1);
          if (savedII.length) {
            setSavedAifII((prev) => [
              ...prev,
              ...savedII.map((r) => ({
                fund_name: selectedFundName,
                class: r.class_code,
                size: r.size || "",
                management_fee: r.management_fee_sharing_per ?? "",
                placement_fee: r.placement_fee_per ?? "",
                upfront_fee: r.upfront_fee_per ?? "",
                trail_fee: r.trail_fee_per ?? "",
                total_fee: r.total_fee_per ?? "",
                product_id: r.product_id,
                fund_id: r.fund_id || selectedFund.fund_id,
                is_active: r.is_active ?? true,
              })),
            ]);
          }
          const newData = [...aifCatIIData];
          newData.splice(index, 1);
          setAifCatIIData(newData);

        } else if (tableType === "aifIII") {
          const savedIII = saved.filter((s) => Number(s.product_type_id) === 2);
          if (savedIII.length) {
            setSavedAifIII((prev) => [
              ...prev,
              ...savedIII.map((r) => ({
                fund_name: selectedFundName,
                class: r.class_code,
                management_fee: r.management_fee_sharing_per ?? "",
                performance_fee: r.performance_fee_sharing_per ?? "",
                product_id: r.product_id,
                fund_id: r.fund_id || selectedFund.fund_id,
                is_active: r.is_active ?? true,
              })),
            ]);
          }
          const newData = [...aifCatIIIData];
          newData.splice(index, 1);
          setAifCatIIIData(newData);

        } else if (tableType === "pms") {
          const savedPmsData = saved.filter((s) => Number(s.product_type_id) === 3);
          if (savedPmsData.length) {
            setSavedPms((prev) => [
              ...prev,
              ...savedPmsData.map((r) => ({
                fund_name: selectedFundName,
                class: r.class_code,
                management_fee: r.management_fee_sharing_per ?? "",
                management_fee_more_than_15cr: r.management_fee_sharing_per_morethan15Cr || r.management_fee_sharing_per_morethan15cr || "",
                distributor_sharing_fee: r.performance_fee_sharing_per || r.distributor_sharing_fee_per || "",
                product_id: r.product_id,
                fund_id: r.fund_id || selectedFund.fund_id,
                is_active: r.is_active ?? true,
              })),
            ]);
          }
          const newData = [...pmsData];
          newData.splice(index, 1);
          setPmsData(newData);
        }
      } else {
        toast.error(res.data?.Message || "Failed to save fund details");
      }
    } catch (err) {
      console.error("handleRowAdd error:", err);
      toast.error("An error occurred while saving.");
    }
  };




  const handleDeleteFund = async (fund, type) => {
    if (isReadOnly) {
      toast.warn("Form is read-only. Action not allowed.");
      return;
    }

    // Optimistic UI update or wait for API? Let's wait for API for safety
    try {
      const payload = {
        distributor_sequence_number: distributorSequenceNumber,
        fund_id: fund.fund_id,
        class_code: fund.class || fund.class_code,
        product_type_id: type === "aifII" ? 1 : type === "aifIII" ? 2 : 3,
        product_id: fund.product_id
      };

      const res = await post("/sales_listing/deleteFundDetail", payload);

      if (res.data?.Success) {
        toast.success("Fund deleted successfully");

        // Update local state
        if (type === "aifII") {
          setSavedAifII((prev) =>
            prev.filter(
              (f) => !(f.fund_name === fund.fund_name && f.class === fund.class)
            )
          );
        } else if (type === "aifIII") {
          setSavedAifIII((prev) =>
            prev.filter(
              (f) => !(f.fund_name === fund.fund_name && f.class === fund.class)
            )
          );
        } else if (type === "pms") {
          setSavedPms((prev) =>
            prev.filter(
              (f) => !(f.fund_name === fund.fund_name && f.class === fund.class)
            )
          );
        }
      } else {
        toast.error(res.data?.Message || "Failed to delete fund");
      }
    } catch (error) {
      console.error("Error deleting fund:", error);
      toast.error("An error occurred while deleting.");
    }
  };

  // Inline Edit State
  const [editingRow, setEditingRow] = useState(null); // { type, index }

  const handleEditRow = (type, index) => {
    setEditingRow({ type, index });
  };

  const handleSavedRowChange = (type, index, field, value) => {
    if (type === "aifII") {
      setSavedAifII((prev) => {
        const newData = [...prev];
        const updatedRow = { ...newData[index], [field]: value };

        // Auto-calculate fees logic (same as handleFundInputChange)
        if (field === "placement_fee") {
          const placementFee = parseFloat(value) || 0;
          if (placementFee > 0) {
            const upfrontFee = (placementFee / 3).toFixed(3);
            const trailFee = (placementFee - parseFloat(upfrontFee)).toFixed(3);
            updatedRow.upfront_fee = upfrontFee;
            updatedRow.trail_fee = trailFee;
          } else {
            updatedRow.upfront_fee = "";
            updatedRow.trail_fee = "";
          }
        }

        newData[index] = updatedRow;
        return newData;
      });
    } else if (type === "aifIII") {
      setSavedAifIII((prev) => {
        const newData = [...prev];
        newData[index] = { ...newData[index], [field]: value };
        return newData;
      });
    } else if (type === "pms") {
      setSavedPms((prev) => {
        const newData = [...prev];
        newData[index] = { ...newData[index], [field]: value };
        return newData;
      });
    }
  };

  const handleSaveRow = async (type, index) => {
    let rowData;
    let productTypeId;

    if (type === "aifII") {
      rowData = savedAifII[index];
      productTypeId = 1;
    } else if (type === "aifIII") {
      rowData = savedAifIII[index];
      productTypeId = 2;
    } else if (type === "pms") {
      rowData = savedPms[index];
      productTypeId = 3;
    }

    if (!rowData) return;

    // Construct Payload for /editFundDetail
    const payload = {
      distributor_sequence_number: distributorSequenceNumber,
      fund_id: rowData.fund_id,
      class_code: rowData.class || rowData.class_code, // Handle both key names if inconsistent
      product_type_id: productTypeId,
      product_id: rowData.product_id,
      management_fee_sharing_per: rowData.management_fee,
      performance_fee_sharing_per: rowData.performance_fee || rowData.distributor_sharing_fee, // Performance fee for AIF3, Dist Share for PMS (mapped to perf fee column in DB?) - Check backend query
      // Backend query uses: performance_fee_sharing_per (mapped to distributor_sharing_fee in PMS UI logic?)
      // Let's check PMS logic: "distributor_sharing_fee" mapped to "distributor_sharing_fee_per" in DB?
      // Backend UPDATE query sets "performance_fee_sharing_per". In PMS insert it was setting "performance_fee_sharing_per" to "distributor_sharing_fee".
      // Let's assume standardization:
      placement_fee_per: rowData.placement_fee,
      size: rowData.size,
      total_fee_per: rowData.total_fee,
      trail_fee_per: rowData.trail_fee,
      upfront_fee_per: rowData.upfront_fee,
    };

    // Specific mapping tweaks based on type to match backend expectation if needed
    if (type === "pms") {
      payload.performance_fee_sharing_per = rowData.distributor_sharing_fee; // Map dist share to perf fee col
      payload.management_fee_sharing_per_morethan15cr = rowData.management_fee_more_than_15cr; // Map new field
    }

    try {
      const res = await post("/sales_listing/editFundDetail", payload);
      if (res.data?.Success) {
        toast.success("Fund details updated successfully");
        setEditingRow(null);
      } else {
        toast.error(res.data?.Message || "Failed to update fund details");
      }
    } catch (error) {
      console.error("Error updating fund:", error);
      toast.error("An error occurred while updating.");
    }
  };





  // Stable handlers ref to avoid re-creating columns on every render (which kills focus)
  const handlersRef = useRef({
    handleEditRow,
    handleSavedRowChange,
    handleSaveRow,
    handleDeleteFund
  });

  // Keep ref updated with latest closures
  handlersRef.current = {
    handleEditRow,
    handleSavedRowChange,
    handleSaveRow,
    handleDeleteFund
  };

  const aifCatIIColumns = useMemo(() => [
    {
      Header: "Fund Name",
      accessor: "fund_name",
      size: 200,
      Cell: ({ value }) => <div className="px-2 py-1 whitespace-normal break-words">{value || "N/A"}</div>,
    },
    {
      Header: "Class",
      accessor: "class",
      size: 200,
      Cell: ({ value }) => <div className="px-2 py-1">{value || "N/A"}</div>,
    },
    {
      Header: "Contribution (In CR)",
      accessor: "size",
      size: 200,
      Cell: ({ row, value }) => {
        return <div className="px-2 py-1">{value || "N/A"}</div>;
      },
    },
    {
      Header: "Management Fee %",
      accessor: "management_fee",
      size: 250,
      Cell: ({ row, value }) => {
        return <div className="px-2 py-1">{value || "N/A"}</div>;
      },
    },
    {
      Header: "Distributor Sharing %",
      accessor: "placement_fee",
      size: 250,
      Cell: ({ row, value }) => {
        const isEditing = editingRow?.type === "aifII" && editingRow?.index === row.index;
        return isEditing ? (
          <input
            value={value}
            onChange={(e) => handlersRef.current.handleSavedRowChange("aifII", row.index, "placement_fee", e.target.value)}
            className="border rounded px-2 py-1 w-full"
          />
        ) : (
          <div className="px-2 py-1">{formatPercentValue(value) || "N/A"}</div>
        );
      },
    },
    {
      Header: "Upfront Fee %",
      accessor: "upfront_fee",
      size: 200,
      Cell: ({ value }) => <div className="px-2 py-1">{value || "N/A"}</div>,
    },
    {
      Header: "Trail Fee %",
      accessor: "trail_fee",
      size: 200,
      Cell: ({ value }) => <div className="px-2 py-1">{value || "N/A"}</div>,
    },
    {
      Header: "Action",
      accessor: "action",
      size: 140, // Expanded for buttons
      Cell: ({ row }) => {
        const isEditing = editingRow?.type === "aifII" && editingRow?.index === row.index;
        return (
          <div className="flex gap-2">
            {!isEditing ? (
              <>
                <Button
                  size="sm"
                  variant=""
                  onClick={() => handlersRef.current.handleEditRow("aifII", row.index)}
                  className="border border-gray-100 hover:bg-indigo-50 hover:text-indigo-600 text-gray-600 px-3 py-1.5 rounded-lg transition-all duration-300 shadow-sm"
                  disabled={isReadOnly}
                  title="Edit"
                >
                  <SquarePen size={14} />
                </Button>
                <Button
                  size="sm"
                  variant=""
                  onClick={() => handlersRef.current.handleDeleteFund(row.original, "aifII")}
                  className="border border-red-100 text-red-500 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-all duration-300"
                  disabled={isReadOnly}
                  title="Delete"
                >
                  <X size={14} />
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                variant=""
                onClick={() => handlersRef.current.handleSaveRow("aifII", row.index)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1.5 rounded-lg shadow-md shadow-indigo-100 transition-all duration-300 flex items-center gap-2 transform hover:-translate-y-0.5"
                title="Save"
              >
                <Save size={14} className="mr-1" /> Save
              </Button>
            )}
          </div>
        );
      },
    },
  ], [editingRow, isReadOnly]);

  const aifCatIIIColumns = useMemo(() => [
    {
      Header: "Fund Name",
      accessor: "fund_name",
      size: 200,
      Cell: ({ value }) => <div className="px-2 py-1 whitespace-normal break-words">{value || "N/A"}</div>,
    },
    {
      Header: "Class",
      accessor: "class",
      size: 200,
      Cell: ({ value }) => <div className="px-2 py-1">{value || "N/A"}</div>,
    },
    {
      Header: "Management Fee %",
      accessor: "management_fee",
      size: 250,
      Cell: ({ row, value }) => {
        return <div className="px-2 py-1">{value || "N/A"}</div>;
      },
    },
    {
      Header: "Distributor Sharing %",
      accessor: "performance_fee",
      size: 250,
      Cell: ({ row, value }) => {
        const isEditing = editingRow?.type === "aifIII" && editingRow?.index === row.index;
        return isEditing ? (
          <input
            value={value}
            onChange={(e) => handlersRef.current.handleSavedRowChange("aifIII", row.index, "performance_fee", e.target.value)}
            className="border rounded px-2 py-1 w-full"
          />
        ) : (
          <div className="px-2 py-1">{formatPercentValue(value) || "N/A"}</div>
        );
      },
    },
    {
      Header: "Action",
      accessor: "action",
      size: 140,
      Cell: ({ row }) => {
        const isEditing = editingRow?.type === "aifIII" && editingRow?.index === row.index;
        return (
          <div className="flex gap-2">
            {!isEditing ? (
              <>
                <Button
                  size="sm"
                  variant=""
                  onClick={() => handlersRef.current.handleEditRow("aifIII", row.index)}
                  className="border border-gray-100 hover:bg-indigo-50 hover:text-indigo-600 text-gray-600 px-3 py-1.5 rounded-lg transition-all duration-300 shadow-sm"
                  disabled={isReadOnly}
                  title="Edit"
                >
                  <SquarePen size={14} />
                </Button>
                <Button
                  size="sm"
                  variant=""
                  onClick={() => handlersRef.current.handleDeleteFund(row.original, "aifIII")}
                  className="border border-red-100 text-red-500 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-all duration-300"
                  disabled={isReadOnly}
                  title="Delete"
                >
                  <X size={14} />
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                variant=""
                onClick={() => handlersRef.current.handleSaveRow("aifIII", row.index)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1.5 rounded-lg shadow-md shadow-indigo-100 transition-all duration-300 flex items-center gap-2 transform hover:-translate-y-0.5"
                title="Save"
              >
                <Save size={14} className="mr-1" /> Save
              </Button>
            )}
          </div>
        );
      },
    },
  ], [editingRow, isReadOnly]);

  const pmsColumns = useMemo(() => [
    {
      Header: "Fund Name",
      accessor: "fund_name",
      size: 200,
      Cell: ({ value }) => <div className="px-2 py-1 whitespace-normal break-words">{value || ""}</div>,
    },
    {
      Header: "Class",
      accessor: "class",
      size: 200,
      Cell: ({ value }) => <div className="px-2 py-1">{value || ""}</div>,
    },
    {
      Header: "Management Fee less than 15 Cr %",
      accessor: "management_fee",
      size: 250,
      Cell: ({ row, value }) => {
        return <div className="px-2 py-1">{value || ""}</div>;
      },
    },
    {
      Header: "Management Fee more than 15 Cr %",
      accessor: "management_fee_more_than_15cr",
      size: 250,
      Cell: ({ row, value }) => {
        return <div className="px-2 py-1">{value || ""}</div>;
      },
    },
    {
      Header: "Distributor Sharing Fee %",
      accessor: "distributor_sharing_fee",
      size: 250,
      Cell: ({ row, value }) => {
        const isEditing = editingRow?.type === "pms" && editingRow?.index === row.index;
        return isEditing ? (
          <input
            value={value}
            onChange={(e) => handlersRef.current.handleSavedRowChange("pms", row.index, "distributor_sharing_fee", e.target.value)}
            className="border rounded px-2 py-1 w-full"
          />
        ) : (
          <div className="px-2 py-1">{formatPercentValue(value)}</div>
        );
      },
    },
    {
      Header: "Action",
      accessor: "action",
      size: 140,
      Cell: ({ row }) => {
        const isEditing = editingRow?.type === "pms" && editingRow?.index === row.index;
        return (
          <div className="flex gap-2">
            {!isEditing ? (
              <>
                <Button
                  size="sm"
                  variant=""
                  onClick={() => handlersRef.current.handleEditRow("pms", row.index)}
                  className="border border-gray-100 hover:bg-indigo-50 hover:text-indigo-600 text-gray-600 px-3 py-1.5 rounded-lg transition-all duration-300 shadow-sm"
                  disabled={isReadOnly}
                  title="Edit"
                >
                  <SquarePen size={14} />
                </Button>
                <Button
                  size="sm"
                  variant=""
                  onClick={() => handlersRef.current.handleDeleteFund(row.original, "pms")}
                  className="border border-red-100 text-red-500 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-all duration-300"
                  disabled={isReadOnly}
                  title="Delete"
                >
                  <X size={14} />
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                variant=""
                onClick={() => handlersRef.current.handleSaveRow("pms", row.index)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1.5 rounded-lg shadow-md shadow-indigo-100 transition-all duration-300 flex items-center gap-2 transform hover:-translate-y-0.5"
                title="Save"
              >
                <Save size={14} className="mr-1" /> Save
              </Button>
            )}
          </div>
        );
      },
    },
  ], [editingRow, isReadOnly]);

  const handleSaveEdits = async () => {
    try {
      const payload = {
        distributor_sequence_number: distributorSequenceNumber,
        arn_code: arnCode === "APRN" ? "" : arnCode, // Added APRN code to payload
        sales_name: userDetails?.name || userDetails?.username || "",
      };

      const res = await post("/sales_listing/editSalesListing", payload);

      if (res.data?.Success) {
        toast.success("Edits saved successfully");
        navigate("/sales_listing");
      } else {
        toast.error(res.data?.Message || "Failed to save edits");
      }
    } catch (error) {
      console.error("Error saving edits:", error);
      toast.error("An error occurred while saving.");
    }
  };

  return (
    <div className="max-w-screen-xl mx-auto p-4">
      <style>{`
        .sales-distributor-dropdown.p-dropdown {
          width: 100%;
          height: 40px;
          border: 1px solid #d1d5db;
          border-radius: 0.375rem;
          background: #fff;
          box-shadow: none;
          transition: border-color 0.2s ease;
        }

        .sales-distributor-dropdown.p-dropdown:not(.p-disabled):hover {
          border-color: #d1d5db;
        }

        .sales-distributor-dropdown.p-dropdown:not(.p-disabled).p-focus {
          border-color: #d1d5db;
          box-shadow: none;
        }

        .sales-distributor-dropdown .p-dropdown-label {
          padding: 0.5rem 0.75rem;
          font-size: 1rem;
          color: #111827;
          display: flex;
          align-items: center;
        }

        .sales-distributor-dropdown .p-dropdown-label.p-placeholder {
          color: #9ca3af;
        }

        .sales-distributor-dropdown .p-dropdown-trigger,
        .sales-distributor-dropdown .p-dropdown-clear-icon {
          color: #9ca3af;
        }

        .sales-distributor-dropdown .p-dropdown-trigger {
          width: 2.5rem;
        }

        .sales-distributor-dropdown .p-dropdown-clear-icon {
          display: none;
        }

        .sales-distributor-dropdown-panel.p-dropdown-panel {
          margin-top: 0.25rem;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          overflow: hidden;
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.1);
          background: #ffffff;
        }

        .sales-distributor-dropdown-panel .p-dropdown-header {
          padding: 0.625rem;
          background: #fff;
          border-bottom: 1px solid #f3f4f6;
          position: relative;
        }

        .sales-distributor-dropdown-panel .p-dropdown-filter {
          height: 40px;
          border: 1px solid #d1d5db;
          border-radius: 0.375rem;
          padding: 0.5rem 0.875rem 0.5rem 2.75rem;
          font-size: 0.95rem;
          color: #111827;
          box-shadow: none;
          width: 100%;
          line-height: 1.25rem;
        }

        .sales-distributor-dropdown-panel .p-dropdown-filter:focus {
          border-color: #c7d2fe;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
          outline: none;
        }

        .sales-distributor-dropdown-panel .p-dropdown-filter-icon {
          position: absolute;
          left: 1.35rem;
          top: 50%;
          transform: translateY(-50%);
          width: 1rem;
          height: 1rem;
          color: #9ca3af;
          pointer-events: none;
          margin-top: 0;
        }

        .sales-distributor-dropdown-panel .p-dropdown-wrapper {
          max-height: 260px;
        }

        .sales-distributor-dropdown-panel .p-dropdown-items {
          padding: 0.5rem 0.375rem;
        }

        .sales-distributor-dropdown-panel .p-dropdown-item {
          padding: 0.75rem 1rem;
          font-size: 0.95rem;
          color: #111827;
          font-weight: 400;
          border-radius: 0.375rem;
          margin: 0 0 0.125rem;
          border: none;
          outline: none;
          box-shadow: none;
          line-height: 1.35rem;
        }

        .sales-distributor-dropdown-panel .p-dropdown-item:hover {
          background: #f8fafc;
        }

        .sales-distributor-dropdown-panel .p-dropdown-item:focus,
        .sales-distributor-dropdown-panel .p-dropdown-item.p-focus {
          outline: none !important;
          box-shadow: none !important;
          border: none !important;
          background: #f8fafc;
        }

        .sales-distributor-dropdown-panel .p-dropdown-item.p-highlight {
          background: #eef2ff;
          color: #4338ca;
          font-weight: 500;
        }

        .sales-distributor-dropdown-panel .p-dropdown-empty-message {
          padding: 1rem 0.75rem;
          color: #9ca3af;
          text-align: center;
        }

        .sales-distributor-dropdown-panel .p-dropdown-items-wrapper {
          padding: 0;
          outline: none;
        }

        .sales-distributor-dropdown-panel .p-dropdown-items-wrapper::-webkit-scrollbar {
          width: 8px;
        }

        .sales-distributor-dropdown-panel .p-dropdown-items-wrapper::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 9999px;
        }

        .sales-distributor-dropdown-panel .p-dropdown-items-wrapper::-webkit-scrollbar-track {
          background: transparent;
        }
      `}</style>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="bg-white border rounded-md shadow p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
            {/* Distributor Name */}
            <div className="flex flex-col">
              <label className="font-medium text-gray-700 mb-1">
                Distributor Name <span className="text-red-500">*</span>
              </label>
              <Dropdown
                value={distributor}
                onChange={(e) => handleDistributorChange(e.value)}
                options={sortedDistributorList}
                optionLabel="name"
                optionValue="name"
                placeholder="Select distributor"
                filter
                filterBy="name"
                filterPlaceholder="Search distributor..."
                emptyFilterMessage="No distributors found"
                disabled={isDistributorReadOnly}
                className={`sales-distributor-dropdown ${isDistributorReadOnly ? "opacity-100" : ""}`}
                panelClassName="sales-distributor-dropdown-panel"
              />
            </div>

            {/* APRN Code */}
            <div className="flex flex-col min-h-[84px]">
              <label className="font-medium text-gray-700 mb-1">APRN Code</label>
              <input
                type="text"
                value={arnCode}
                onChange={handleArnCodeChange}
                onBlur={validateArnCode}
                // Remove isDistributorReadOnly check to allow editing
                readOnly={false}
                maxLength={9}
                placeholder="APRN12345"
                className={`border p-2 rounded-md w-full ${arnCodeError ? "border-red-400 focus:border-red-400" : "border-gray-300"
                  }`}
              />
              <span className={`mt-1 text-xs ${arnCodeError ? "text-red-500" : "text-transparent"}`}>
                {arnCodeError || "APRN format placeholder"}
              </span>
            </div>

            {/* Distributor Address */}
            <div className="flex flex-col">
              <label className="font-medium text-gray-700 mb-1">
                Address <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Enter Address"
                readOnly={isDistributorReadOnly}
                className={`border border-gray-300 p-2 rounded-md w-full ${isDistributorReadOnly ? "bg-gray-100" : ""}`}
              />
            </div>

            {/* Country */}
            <div className="flex flex-col">
              <label className="font-medium text-gray-700 mb-1">
                Country <span className="text-red-500">*</span>
              </label>
              <Dropdown
                value={country}
                onChange={(e) => handleCountryChange(e.value)}
                options={countryOptions}
                placeholder="Select country"
                disabled={isDistributorReadOnly}
                className={`sales-distributor-dropdown ${isDistributorReadOnly ? "opacity-100" : ""}`}
                panelClassName="sales-distributor-dropdown-panel"
              />
            </div>

            {/* Pin Code */}
            <div className="flex flex-col">
              <label className="font-medium text-gray-700 mb-1">
                {isIndia ? "Pin Code" : "ZIP / Postal Code"} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={pinCode}
                onChange={(e) =>
                  setPinCode(
                    isIndia
                      ? e.target.value.replace(/\D/g, "").slice(0, 6)
                      : e.target.value.replace(/[^A-Za-z0-9 -]/g, "").toUpperCase().slice(0, 10)
                  )
                }
                placeholder={isIndia ? "Enter Pin Code" : "Enter ZIP / Postal Code"}
                maxLength={isIndia ? 6 : 10}
                readOnly={isDistributorReadOnly}
                className={`border border-gray-300 p-2 rounded-md w-full ${isDistributorReadOnly ? "bg-gray-100" : ""}`}
              />
            </div>

            {/* City */}
            <div className="flex flex-col">
              <label className="font-medium text-gray-700 mb-1">
                City <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder={isIndia ? (pinLookupLoading ? "Fetching City..." : "City auto-filled from Pin Code") : "Enter City"}
                readOnly={isIndia || isDistributorReadOnly}
                className={`border border-gray-300 p-2 rounded-md w-full ${(isIndia || isDistributorReadOnly) ? "bg-gray-100" : ""}`}
              />
            </div>

            {/* State */}
            <div className="flex flex-col">
              <label className="font-medium text-gray-700 mb-1">
                State <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value)}
                placeholder={isIndia ? (pinLookupLoading ? "Fetching State..." : "State auto-filled from Pin Code") : "Enter State / Province"}
                readOnly={isIndia || isDistributorReadOnly}
                className={`border border-gray-300 p-2 rounded-md w-full ${(isIndia || isDistributorReadOnly) ? "bg-gray-100" : ""}`}
              />
            </div>

            {/* PAN Number */}
            <div className="flex flex-col">
              <label className="font-medium text-gray-700 mb-1">
                {isIndia ? "PAN Number" : "PAN / Tax ID"} {isIndia && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                value={panNumber}
                onChange={(e) => setPanNumber(e.target.value.toUpperCase())}
                placeholder={isIndia ? "Enter PAN Number" : "Enter PAN / Tax ID (optional)"}
                maxLength={isIndia ? 10 : 20}
                readOnly={!isPanEditable || isDistributorReadOnly}
                className={`border border-gray-300 p-2 rounded-md w-full ${(!isPanEditable || isDistributorReadOnly) ? "bg-gray-100" : ""}`}
              />
            </div>

            <div className="flex flex-col md:col-span-2">
              <label className="font-medium text-gray-700 mb-1">Email IDs <span className="text-red-500">*</span></label>
              <div className="flex">
                <input
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="abc@xyz.com; def@xyz.com"
                  readOnly={isDistributorReadOnly}
                  className={`border border-gray-300 p-2 rounded-md flex-1 ${isDistributorReadOnly ? "bg-gray-100" : ""}`}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-md shadow p-4 h-fit">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-800">Approved Funds</h3>
              <p className="text-xs text-gray-400">
                Funds mapped for approved and signed agreements
              </p>
            </div>
            <span className="inline-flex min-w-[32px] justify-center rounded-full bg-indigo-50 px-2 py-1 text-xs font-bold text-indigo-600">
              {approvedFunds.length}
            </span>
          </div>

          {!distributor ? (
            <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
              Select a distributor to view available funds.
            </div>
          ) : approvedFundsLoading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-10 text-gray-400">
              <div className="h-7 w-7 rounded-full border-2 border-gray-200 border-t-indigo-500 animate-spin" />
              <span className="text-xs font-semibold uppercase tracking-widest">
                Fetching funds...
              </span>
            </div>
          ) : approvedFunds.length > 0 ? (
            <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
              {approvedFunds.map((fund) => (
                <div
                  key={`${fund.fund_id}-${fund.fund_name}`}
                  className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
                >
                  <p className="text-sm font-semibold text-gray-800">
                    {fund.fund_name || "-"}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-amber-200 bg-amber-50 px-4 py-8 text-center text-sm text-amber-700">
              No approved funds found for this distributor.
            </div>
          )}
        </div>
      </div>
      <div className="max-w-screen-xl mx-auto p-4 bg-white rounded shadow space-y-6 mt-6">
        <h2 className="text-md font-semibold underline text-left">
          Fund Details
        </h2>

        {/* Only show selectors and input fields if not readOnly */}
        {!isReadOnly && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Fund Type Selector */}
              <div className="col-span-1">
                <label className="block font-medium mb-1">Fund Type</label>
                <select
                  value={fundType}
                  onChange={(e) => {
                    setFundType(e.target.value);
                    setFundTypeForApi(e.target.value);
                    setSelectedFundName("");
                    setClassCodes([]);
                    setSelectedClassCode("");
                    // Clear all data tables on type change
                    setAifCatIIData([]);
                    setAifCatIIIData([]);
                    setPmsData([]);
                  }}
                  className="w-full px-2 border rounded"
                  disabled={
                    savedAifII.length > 0 ||
                    savedAifIII.length > 0 ||
                    savedPms.length > 0
                  }
                >
                  <option value="">Select Fund Type</option>
                  <option value="aif">AIF</option>
                  {isValidArnCode(arnCode) && <option value="pms">PMS</option>}
                </select>
              </div>

              <div className="col-span-1">
                <label className="block font-medium mb-1">Fund Name</label>
                <select
                  value={selectedFundName}
                  onChange={(e) => setSelectedFundName(e.target.value)}
                  className="w-full px-2 border rounded"
                  disabled={!fundNames.length}
                >
                  <option value="">Select Fund Name</option>
                  {fundNames.map((fund) => (
                    <option key={fund.fund_id} value={fund.fund_name}>
                      {fund.fund_name}
                    </option>
                  ))}
                </select>
              </div>


            </div>

            <div className="mt-4">
              {aifCatIIData.length > 0 && aifCatIIData[0].fund_name && (
                <>
                  <h3 className="font-semibold mb-2">
                    AIF CAT II - Enter Fund Details
                  </h3>
                  {aifCatIIData.map((row, index) => (
                    <div
                      key={`aifII-${index}-${row.fund_name}-${row.class}`}
                      className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4 border p-3 rounded-lg"
                    >
                      <InputField
                        label="Fund Name"
                        value={row.fund_name}
                        readOnly
                      />
                      <InputField label="Class" value={row.class} readOnly />
                      {/* Size field - allows special characters */}
                      <InputField
                        label="Contribution (In CR)"
                        value={row.size}
                        onChange={(e) =>
                          handleFundInputChange(
                            "aifII",
                            index,
                            "size",
                            e.target.value
                          )
                        }
                        readOnly={true}
                        className="w-full px-2 border rounded bg-gray-100 cursor-not-allowed"
                        placeholder="Contribution"
                        isNumeric={false} // Allows special characters
                      />
                      {/* All fee fields - numbers only */}
                      <InputField
                        label="Management Fee %"
                        value={row.management_fee}
                        onChange={(e) =>
                          handleFundInputChange(
                            "aifII",
                            index,
                            "management_fee",
                            e.target.value
                          )
                        }
                        readOnly={true}
                        className="w-full px-2 border rounded bg-gray-100 cursor-not-allowed"
                        placeholder="Mgmt Fee %"
                        isNumeric={true} // Numbers only
                      />
                      <InputField
                        label="Distributor Sharing %"
                        value={row.placement_fee}
                        onChange={(e) =>
                          handleFundInputChange(
                            "aifII",
                            index,
                            "placement_fee",
                            e.target.value
                          )
                        }
                        className="w-full px-2 border rounded"
                        placeholder="Distributor Sharing %"
                        isNumeric={true} // Numbers only
                      />
                      <InputField
                        label="Upfront Fee %"
                        value={row.upfront_fee}
                        readOnly // Make upfront fee read-only as it's auto-calculated
                        className="bg-gray-100"
                      />
                      <InputField
                        label="Trail Fee %"
                        value={row.trail_fee}
                        readOnly // Make trail fee read-only as it's auto-calculated
                        className="bg-gray-100"
                      />
                      <div className="flex items-end">
                        <Button
                          variant=""
                          size="sm"
                          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-all duration-300 transform hover:-translate-y-0.5 shadow-md shadow-indigo-100 font-bold uppercase tracking-wider text-[10px] py-2"
                          onClick={() => handleRowAdd("aifII", index)}
                        >
                          Add
                        </Button>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {aifCatIIIData.length > 0 && aifCatIIIData[0].fund_name && (
                <>
                  <h3 className="font-semibold mb-2">
                    AIF CAT III - Enter Fund Details
                  </h3>
                  {aifCatIIIData.map((row, index) => (
                    <div
                      key={`aifIII-${index}-${row.fund_name}-${row.class}`}
                      className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4 border p-3 rounded-lg"
                    >
                      <InputField
                        label="Fund Name"
                        value={row.fund_name}
                        readOnly
                      />
                      <InputField label="Class" value={row.class} readOnly />
                      {/* Fee fields - numbers only */}
                      <InputField
                        label="Management Fee %"
                        value={row.management_fee}
                        onChange={(e) =>
                          handleFundInputChange(
                            "aifIII",
                            index,
                            "management_fee",
                            e.target.value
                          )
                        }
                        readOnly={true}
                        className="w-full px-2 border rounded bg-gray-100 cursor-not-allowed"
                        placeholder="Mgmt Fee %"
                        isNumeric={true} // Numbers only
                      />
                      <InputField
                        label="Distributor Sharing %"
                        value={row.performance_fee}
                        onChange={(e) =>
                          handleFundInputChange(
                            "aifIII",
                            index,
                            "performance_fee",
                            e.target.value
                          )
                        }
                        className="w-full px-2 border rounded"
                        placeholder="Distributor Sharing %"
                        isNumeric={true} // Numbers only
                      />
                      <div className="flex items-end">
                        <Button
                          variant=""
                          size="sm"
                          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-all duration-300 transform hover:-translate-y-0.5 shadow-md shadow-indigo-100 font-bold uppercase tracking-wider text-[10px] py-2"
                          onClick={() => handleRowAdd("aifIII", index)}
                        >
                          Add
                        </Button>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {pmsData.length > 0 && pmsData[0].fund_name && (
                <>
                  <h3 className="font-semibold mb-2">
                    PMS - Enter Fund Details
                  </h3>
                  {pmsData.map((row, index) => (
                    <div
                      key={`pms-${index}-${row.fund_name}-${row.class}`}
                      className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4 border p-3 rounded-lg"
                    >
                      <InputField
                        label="Fund Name"
                        value={row.fund_name}
                        readOnly
                      />
                      <InputField label="Class" value={row.class} readOnly />
                      {/* Fee fields - numbers only */}
                      <InputField
                        label="Management Fee less than 15 Cr %"
                        value={row.management_fee}
                        onChange={(e) =>
                          handleFundInputChange(
                            "pms",
                            index,
                            "management_fee",
                            e.target.value
                          )
                        }
                        readOnly={true}
                        className="w-full px-2 border rounded bg-gray-100 cursor-not-allowed"
                        placeholder="Mgmt Fee (<15 Cr) %"
                        isNumeric={true} // Numbers only
                      />
                      <InputField
                        label="Management Fee more than 15 Cr %"
                        value={row.management_fee_more_than_15cr}
                        onChange={(e) =>
                          handleFundInputChange(
                            "pms",
                            index,
                            "management_fee_more_than_15cr",
                            e.target.value
                          )
                        }
                        readOnly={true}
                        className="w-full px-2 border rounded bg-gray-100 cursor-not-allowed"
                        placeholder="Mgmt Fee (>15 Cr) %"
                        isNumeric={true} // Numbers only
                      />
                      <InputField
                        label="Distributor Sharing Fee %"
                        value={row.distributor_sharing_fee}
                        onChange={(e) =>
                          handleFundInputChange(
                            "pms",
                            index,
                            "distributor_sharing_fee",
                            e.target.value
                          )
                        }
                        isNumeric={true} // Numbers only
                      />
                      <div className="flex items-end">
                        <Button
                          variant=""
                          size="sm"
                          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-all duration-300 transform hover:-translate-y-0.5 shadow-md shadow-indigo-100 font-bold uppercase tracking-wider text-[10px] py-2"
                          onClick={() => handleRowAdd("pms", index)}
                        >
                          Add
                        </Button>
                      </div>
                    </div>
                  ))}
                </>
              )}



            </div>
          </>
        )
        }

        {/* ✅ FIXED: Display saved fund data for all types */}
        <div className="mt-6">
          {/* AIF CAT II Table */}
          {(savedAifII.length > 0 || isReadOnly) && (
            <div className="mb-6">
              <h3 className="font-semibold mb-2">AIF CAT II - Added Funds</h3>
              {savedAifII.length > 0 ? (
                <ReusableTable columns={aifCatIIColumns} data={savedAifII} />
              ) : (
                <p className="text-gray-500 text-center py-4">
                  No AIF CAT II funds added
                </p>
              )}
            </div>
          )}

          {/* AIF CAT III Table */}
          {(savedAifIII.length > 0 || isReadOnly) && (
            <div className="mb-6">
              <h3 className="font-semibold mb-2">AIF CAT III - Added Funds</h3>
              {savedAifIII.length > 0 ? (
                <ReusableTable columns={aifCatIIIColumns} data={savedAifIII} />
              ) : (
                <p className="text-gray-500 text-center py-4">
                  No AIF CAT III funds added
                </p>
              )}
            </div>
          )}

          {/* PMS Table */}
          {(savedPms.length > 0 || isReadOnly) && (
            <div className="mb-6">
              <h3 className="font-semibold mb-2">PMS - Added Funds</h3>
              {savedPms.length > 0 ? (
                <ReusableTable columns={pmsColumns} data={savedPms} />
              ) : (
                <p className="text-gray-500 text-center py-4">
                  No PMS funds added
                </p>
              )}
            </div>
          )}
        </div>

        {
          !isReadOnly && (
            <div className="flex justify-center mt-8 pb-4">
              {isDistributorReadOnly ? (
                <Button
                  onClick={handleSaveEdits}
                  variant=""
                  size="sm"
                  className="px-10 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-lg shadow-indigo-200 transition-all duration-300 transform hover:-translate-y-1 font-bold tracking-wide py-4"
                >
                  SAVE EDITS
                </Button>
              ) : (
                <Button
                  onClick={handleSubmitWithFunds}
                  variant=""
                  size="sm"
                  className="px-10 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-lg shadow-indigo-200 transition-all duration-300 transform hover:-translate-y-1 font-bold tracking-wide py-4 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none disabled:transform-none disabled:shadow-none"
                  disabled={loading || (savedAifII.length === 0 && savedAifIII.length === 0 && savedPms.length === 0)}
                >
                  {loading ? "SUBMITTING..." : "SUBMIT"}
                </Button>
              )}
            </div>
          )
        }
      </div >
      <ToastContainer position="top-right" autoClose={3000} />
    </div >
  );
};

export default SalesDashboard;
